// ============================================================================
// Ad Template Types
// ============================================================================
// Purpose: TypeScript interfaces for ad template feature
// ============================================================================

/**
 * Sitelink item for template sitelinks
 */
export interface SitelinkItem {
  label: string;
  url: string;
}

/**
 * Creative enhancement fields for Single Image ads
 */
export interface ImageEnhancements {
  image_relevant_comments: boolean | null;
  image_visual_touchups: boolean | null;
  image_text_improvements: boolean | null;
  image_add_overlays: boolean | null;
  image_brightness_contrast: boolean | null;
  image_music: boolean | null;
  image_animation: boolean | null;
  image_site_links: boolean | null;
  image_enhance_cta: boolean | null;
  image_reveal_details: boolean | null;
  image_flex_media: boolean | null;
  image_translate_text: boolean | null;
  image_show_summary: boolean | null;
  image_biz_ai: boolean | null;
  image_replace_media_text: boolean | null;
  image_show_destination_blurbs: boolean | null;
}

/**
 * Creative enhancement fields for Single Video ads
 */
export interface VideoEnhancements {
  video_relevant_comments: boolean | null;
  video_visual_touchups: boolean | null;
  video_text_improvements: boolean | null;
  video_add_effects: boolean | null;
  video_site_links: boolean | null;
  video_enhance_cta: boolean | null;
  video_reveal_details: boolean | null;
  video_flex_media: boolean | null;
  video_translate_text: boolean | null;
  video_show_summary: boolean | null;
  video_biz_ai: boolean | null;
  video_show_destination_blurbs: boolean | null;
}

/**
 * Database representation of an ad template
 */
export interface AdTemplate extends ImageEnhancements, VideoEnhancements {
  catalog_enhancements?: Record<string, boolean> | unknown;
  id: string;
  user_id: string;
  name: string;
  description: string | null;
  ad_account_id: string | null;
  
  // Primary Texts (up to 5)
  primary_text_1: string | null;
  primary_text_2: string | null;
  primary_text_3: string | null;
  primary_text_4: string | null;
  primary_text_5: string | null;
  
  // Headlines (up to 5)
  headline_1: string | null;
  headline_2: string | null;
  headline_3: string | null;
  headline_4: string | null;
  headline_5: string | null;
  
  // CTA & URL
  cta_button: string | null;
  url_destination: string | null;
  url_parameters: string | null;
  
  // Facebook & Instagram Pages
  facebook_page_name: string | null;
  facebook_page_id: string | null;
  instagram_name: string | null;
  instagram_id: string | null;
  
  // Partner Pages
  partner_facebook_page_name: string | null;
  partner_facebook_page_id: string | null;
  partner_instagram_name: string | null;
  partner_instagram_id: string | null;
  
  // Pixel
  pixel_id: string | null;
  pixel_name: string | null;
  
  // Sitelinks (stored as JSON in database)
  sitelinks: unknown;
  
  // Catalog Configuration
  catalog_feed_id: string | null;
  catalog_product_set_id: string | null;
  
  // Optimizations (separate for image and video)
  image_optimize_website_destination: boolean | null;
  video_optimize_website_destination: boolean | null;
  
  // Status
  status: string;
  
  created_at: string;
  updated_at: string;
}

/**
 * Form data for creating/updating templates
 * (excludes auto-generated fields like id, timestamps)
 */
export interface AdTemplateFormData extends Partial<ImageEnhancements>, Partial<VideoEnhancements> {
  catalog_enhancements?: Record<string, boolean>;
  name: string;
  description?: string;
  ad_account_id?: string;
  
  primary_text_1?: string;
  primary_text_2?: string;
  primary_text_3?: string;
  primary_text_4?: string;
  primary_text_5?: string;
  
  headline_1?: string;
  headline_2?: string;
  headline_3?: string;
  headline_4?: string;
  headline_5?: string;
  
  cta_button?: string;
  url_destination?: string;
  url_parameters?: string;
  
  facebook_page_name?: string;
  facebook_page_id?: string;
  instagram_name?: string;
  instagram_id?: string;
  
  partner_facebook_page_name?: string;
  partner_facebook_page_id?: string;
  partner_instagram_name?: string;
  partner_instagram_id?: string;
  
  pixel_id?: string;
  pixel_name?: string;
  
  sitelinks?: SitelinkItem[];
  catalog_feed_id?: string;
  catalog_product_set_id?: string;
  
  // Optimizations (separate for image and video)
  image_optimize_website_destination?: boolean;
  video_optimize_website_destination?: boolean;
  
  status?: string;
}

/**
 * Template field configuration for bulk edit
 * Used to track which fields should be imported from template
 */
export interface TemplateFieldSelection {
  ad_account: boolean;
  primary_texts: boolean;
  headlines: boolean;
  cta_button: boolean;
  url_destination: boolean;
  url_parameters: boolean;
  facebook_page: boolean;
  instagram: boolean;
  partner_facebook_page: boolean;
  partner_instagram: boolean;
  status: boolean;
}

/**
 * Bulk edit application settings
 */
export interface BulkEditSettings {
  template_id: string;
  field_selection: TemplateFieldSelection;
  apply_to: 'selected' | 'all'; // Apply to selected rows or all rows
  selected_row_count?: number; // Number of selected rows (if apply_to = 'selected')
}

/**
 * Template list item (for display in lists)
 */
export interface AdTemplateListItem {
  id: string;
  name: string;
  description: string | null;
  ad_account_id: string | null;
  status: string;
  created_at: string;
  updated_at: string;
}

/**
 * Default enhancement values (all off)
 */
export const DEFAULT_IMAGE_ENHANCEMENTS: ImageEnhancements = {
  image_relevant_comments: false,
  image_visual_touchups: false,
  image_text_improvements: false,
  image_add_overlays: false,
  image_brightness_contrast: false,
  image_music: false,
  image_animation: false,
  image_site_links: false,
  image_enhance_cta: false,
  image_reveal_details: false,
  image_flex_media: false,
  image_translate_text: false,
  image_show_summary: false,
  image_biz_ai: false,
  image_replace_media_text: false,
  image_show_destination_blurbs: false,
};

export const DEFAULT_VIDEO_ENHANCEMENTS: VideoEnhancements = {
  video_relevant_comments: false,
  video_visual_touchups: false,
  video_text_improvements: false,
  video_add_effects: false,
  video_site_links: false,
  video_enhance_cta: false,
  video_reveal_details: false,
  video_flex_media: false,
  video_translate_text: false,
  video_show_summary: false,
  video_biz_ai: false,
  video_show_destination_blurbs: false,
};

/**
 * Default values for new template
 */
export const DEFAULT_TEMPLATE_VALUES: Partial<AdTemplateFormData> = {
  status: 'ACTIVE',
  description: '',
  sitelinks: [],
  ...DEFAULT_IMAGE_ENHANCEMENTS,
  ...DEFAULT_VIDEO_ENHANCEMENTS,
};

/**
 * Default field selection (all fields checked)
 */
export const DEFAULT_FIELD_SELECTION: TemplateFieldSelection = {
  ad_account: true,
  primary_texts: true,
  headlines: true,
  cta_button: true,
  url_destination: true,
  url_parameters: true,
  facebook_page: true,
  instagram: true,
  partner_facebook_page: true,
  partner_instagram: true,
  status: true,
};

/**
 * Field labels for display
 */
export const TEMPLATE_FIELD_LABELS: Record<keyof TemplateFieldSelection, string> = {
  ad_account: 'Ad Account',
  primary_texts: 'Primary Texts (all 5 variations)',
  headlines: 'Headlines (all 5 variations)',
  cta_button: 'CTA Button',
  url_destination: 'URL Destination',
  url_parameters: 'URL Parameters',
  facebook_page: 'Facebook Page',
  instagram: 'Instagram Account',
  partner_facebook_page: 'Partner Facebook Page (Optional)',
  partner_instagram: 'Partner Instagram (Optional)',
  status: 'Default Status',
};

/**
 * Image enhancement field labels
 */
export const IMAGE_ENHANCEMENT_LABELS: Record<keyof ImageEnhancements, string> = {
  image_relevant_comments: 'Relevant comments',
  image_visual_touchups: 'Visual touch-ups',
  image_text_improvements: 'Text improvements',
  image_add_overlays: 'Add overlays',
  image_brightness_contrast: 'Brightness & contrast',
  image_music: 'Music',
  image_animation: 'Image animation',
  image_site_links: 'Add site links',
  image_enhance_cta: 'Enhance CTA',
  image_reveal_details: 'Reveal details',
  image_flex_media: 'Flex media',
  image_translate_text: 'Translate text',
  image_show_summary: 'Show summaries',
  image_biz_ai: 'Add business AI',
  image_replace_media_text: 'Enhance media text',
  image_show_destination_blurbs: 'Show spotlights',
};

/**
 * Video enhancement field labels
 */
export const VIDEO_ENHANCEMENT_LABELS: Record<keyof VideoEnhancements, string> = {
  video_relevant_comments: 'Relevant comments',
  video_visual_touchups: 'Visual touch-ups',
  video_text_improvements: 'Text improvements',
  video_add_effects: 'Add video effects',
  video_site_links: 'Add site links',
  video_enhance_cta: 'Enhance CTA',
  video_reveal_details: 'Reveal details',
  video_flex_media: 'Flex media',
  video_translate_text: 'Translate text',
  video_show_summary: 'Show summaries',
  video_biz_ai: 'Add business AI',
  video_show_destination_blurbs: 'Show spotlights',
};

/**
 * Creative enhancement fields for Carousel ads
 */
export interface CarouselEnhancements {
  carousel_relevant_comments: boolean | null;
  carousel_visual_touchups: boolean | null;
  carousel_profile_end_card: boolean | null;
  carousel_expand_image: boolean | null;
  carousel_add_music: boolean | null;
  carousel_highlight_card: boolean | null;
  carousel_dynamic_description: boolean | null;
  carousel_add_business_ai: boolean | null;
  carousel_enhance_cta: boolean | null;
  carousel_site_links: boolean | null;
}

export const DEFAULT_CAROUSEL_ENHANCEMENTS: CarouselEnhancements = {
  carousel_relevant_comments: false,
  carousel_visual_touchups: false,
  carousel_profile_end_card: false,
  carousel_expand_image: false,
  carousel_add_music: false,
  carousel_highlight_card: false,
  carousel_dynamic_description: false,
  carousel_add_business_ai: false,
  carousel_enhance_cta: false,
  carousel_site_links: false,
};

export const CAROUSEL_ENHANCEMENT_LABELS: Partial<Record<keyof CarouselEnhancements, string>> = {
  carousel_relevant_comments: 'Relevant comments',
  carousel_visual_touchups: 'Visual touch-ups',
  carousel_profile_end_card: 'Profile end card',
  carousel_expand_image: 'Expand image',
  carousel_highlight_card: 'Highlight carousel card',
  carousel_dynamic_description: 'Dynamic description',
  carousel_add_business_ai: 'Add business AI',
  carousel_enhance_cta: 'Enhance CTA',
  carousel_site_links: 'Add site links',
};

/**
 * Creative enhancement fields for Collection ads
 * These use OPT_IN / OPT_OUT string values instead of booleans
 */
export interface CollectionEnhancements {
  collection_biz_ai: string | null;
  collection_adapt_to_placement: string | null;
  collection_description_automation: string | null;
  collection_image_background_gen: string | null;
  collection_inline_comment: string | null;
  collection_hide_price: string | null;
  collection_enhance_cta: string | null;
}

export const DEFAULT_COLLECTION_ENHANCEMENTS: CollectionEnhancements = {
  collection_biz_ai: 'OPT_OUT',
  collection_adapt_to_placement: 'OPT_OUT',
  collection_description_automation: 'OPT_OUT',
  collection_image_background_gen: 'OPT_OUT',
  collection_inline_comment: 'OPT_OUT',
  collection_hide_price: 'OPT_OUT',
  collection_enhance_cta: 'OPT_OUT',
};

export const COLLECTION_ENHANCEMENT_LABELS: Record<keyof CollectionEnhancements, string> = {
  collection_biz_ai: 'Add business AI',
  collection_adapt_to_placement: 'Adapt to placements',
  collection_description_automation: 'Dynamic Description',
  collection_image_background_gen: 'Generate backgrounds',
  collection_inline_comment: 'Relevant comments',
  collection_hide_price: 'Hide Price',
  collection_enhance_cta: 'Enhance CTA',
};

/**
 * Catalog enhancement toggle definitions
 * Shared between Setup page and Template form
 */
export interface CatalogEnhancementItem {
  key: string;
  label: string;
  description: string;
}

export const CATALOG_ENHANCEMENTS: CatalogEnhancementItem[] = [
  { key: "biz_ai", label: "Add business AI", description: "Let AI generate additional ad variations" },
  { key: "image_uncrop", label: "Expand image", description: "Expand product images to fit different aspect ratios" },
  { key: "image_background_gen", label: "Generate backgrounds", description: "Generate new backgrounds for product images" },
  { key: "adapt_to_placement", label: "Adapt to placement", description: "Automatically adjust creative for different placements" },
  { key: "add_text_overlay", label: "Add dynamic overlays", description: "Add price, discount, and shipping overlays" },
  { key: "description_automation", label: "Dynamic description", description: "Let AI optimize product descriptions" },
  { key: "inline_comment", label: "Relevant comments", description: "Show relevant social proof comments" },
  { key: "hide_price", label: "Hide price", description: "Hide product price from ad creative" },
  { key: "product_metadata_automation", label: "Add standard labels", description: "Show labels like Free Shipping, In Stock" },
  { key: "enhance_cta", label: "Enhance CTA", description: "Let AI optimize call-to-action buttons" },
  { key: "media_type_automation", label: "Dynamic media", description: "Show images or videos based on what each viewer is likely to engage with" },
  { key: "automated_product_tags", label: "Add product tags", description: "Add interactive product tags to media" },
];

export type CatalogEnhancementValues = Record<string, boolean>;

export const DEFAULT_CATALOG_ENHANCEMENTS: CatalogEnhancementValues = Object.fromEntries(
  CATALOG_ENHANCEMENTS.map((e) => [e.key, false])
);
